import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import DownloadIcon from '@mui/icons-material/Download';
import { visuallyHidden } from '@mui/utils';
import { ExportableReport, ReportService } from '../../services/reportService';
import { downloadFile, readBlobErrorMessage, reportFilename } from '../../utils/downloadFile';

interface ReportExportButtonProps {
  /** Which report to export — the API route segment, and the download filename prefix. */
  report: ExportableReport;
  /** Human name of the report, used to disambiguate the button's accessible name. */
  label: string;
  /** Period start, as the yyyy-MM-dd the date pickers produce. */
  startDate: string;
  /** Period end, as the yyyy-MM-dd the date pickers produce. */
  endDate: string;
  /**
   * True while the panel is refetching. Blocks the export, because until the reload lands the
   * period in these props is not the period on screen — and the export's whole claim is that the
   * file and the page cannot disagree.
   */
  busy?: boolean;
}

/**
 * Server-side CSV export for a report, with its own busy and failure states.
 *
 * One component rather than the same handler copied into four report panels.
 */
export const ReportExportButton: React.FC<ReportExportButtonProps> = ({
  report,
  label,
  startDate,
  endDate,
  busy = false,
}) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const blocked = busy || exporting;

  const handleExport = async () => {
    // aria-disabled rather than the disabled attribute (see the button below), so the guard has to
    // be here as well — an aria-disabled button is still clickable.
    if (blocked) return;

    setExporting(true);
    setError(null);
    setAnnouncement(`Exporting the ${label} report`);
    try {
      const blob = await ReportService.exportReport(report, startDate, endDate);
      downloadFile(blob, reportFilename(report, startDate, endDate));
      setAnnouncement(`${label} report downloaded`);
    } catch (err) {
      const message = await readBlobErrorMessage(err);
      setError(message ?? 'The export was not produced. Try again, or contact support if it persists.');
      setAnnouncement(`${label} export failed`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        onClick={handleExport}
        // aria-disabled, not disabled: setting the DOM attribute the instant the label changes
        // pulls focus off the button a keyboard user just activated, and they land on <body> with
        // no way back but tabbing from the top of the page.
        aria-disabled={blocked}
        aria-busy={exporting}
        // The visible text is "Export CSV" on four different panels, so the accessible name has to
        // say which report. It leads with the visible string because WCAG 2.5.3 requires the
        // accessible name to contain it — otherwise "click Export CSV" matches nothing by voice.
        aria-label={`Export CSV — ${label} report`}
        sx={blocked ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
      >
        {exporting ? 'Exporting…' : 'Export CSV'}
      </Button>

      {/* Success is otherwise announced by nothing at all: a sighted user gets the browser's
          download shelf, a screen-reader user gets a label quietly reverting. */}
      <Box aria-live="polite" sx={visuallyHidden}>{announcement}</Box>

      {error && (
        <Alert severity="warning" onClose={() => setError(null)} sx={{ maxWidth: 420 }}>
          <AlertTitle>Export not produced</AlertTitle>
          {error}
        </Alert>
      )}
    </Box>
  );
};
